import { Dialog, DialogActions, Button, IconButton } from '@material-ui/core/';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import EntityPage from '../../../Layout/EntityPage';
import { IOverrideEntityActions } from '../../../API/Interfaces/IEntityActions';
import LocationsServiceAPI from '../../../API/Services/LocationServiceAPI';
import ISearchBaseDto from '../../../API/Interfaces/ISearchBaseDto';
import { QueryResult, Query, Column } from 'material-table';
import { useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import IGoalDetailsDto, { IGoalBasicDto } from '../../../API/Interfaces/IGoalDtos';
import AppLink from '../../../Layout/Components/AppLink';
import { ConnectIcon } from '../../../Layout/Components/Icons';
import { Helper } from '../../../Utils';
import Modal from '../../../Layout/Components/Modal';
import GoalForm from '../../Goal/GoalForm';
import { onSubmitProps, numberWithSpaces, imageDimensions } from '../../../Constants/constants';
import GoalServiceAPI from '../../../API/Services/GoalServiceAPI';
import { DictImageType } from '../../../API/Interfaces/Enums';

interface IConnectModalProps {
	onClose: () => void;
	isOpen: boolean;
	onRefresh: () => void;
}

export default function ConnectModalForm(props: IConnectModalProps) {
	const { t } = useTranslation();
	const tableRef = useRef();
	const { onClose, isOpen, onRefresh } = props;
	const { id } = useParams<{ id: string }>();
	const { enqueueSnackbar } = useSnackbar();
	const errorDialog = (message?: string) =>
		enqueueSnackbar(`${t('common.errorOccured')}${message ? ': ' : ''}${message && message}`, { variant: 'error' });
	const savedDialog = (isNew = false) => enqueueSnackbar(`${t('common.changesSaved')}`, { variant: 'success' });
	const [open, setOpen] = useState(false);
	const [entity, setEntity] = useState<IGoalDetailsDto>();

	const connectGoal = async (goalId: string) => {
		Helper.setLoader(true);
		const response = await LocationsServiceAPI.ConnectGoalToLocation(id, goalId);

		if (response !== null) {
			Helper.setLoader(false);
			if (response === 1) {
				savedDialog();
				onClose();
				onRefresh();
			} else {
				errorDialog(t(`errorCodes.${response}`));
			}
		} else {
			errorDialog(t('errorCodes.0'));
		}
	};

	const fetchGoal = async function (id: string) {
		try {
			const response = await GoalServiceAPI.GetOne(id);
			if (response) return response;
			else {
				errorDialog();
			}
		} catch (e) {
			errorDialog(e.message);
		}
		return undefined;
	};

	const headers: Array<Column<IGoalBasicDto>> = [
		{
			title: t('repository.goals.goalName'),
			field: 'name',
			defaultSort: 'asc',
			render: (rowData) => {
				return (
					<AppLink
						to={'#'}
						onClick={async () => {
							const response = await fetchGoal(rowData.id);
							if (response) {
								setEntity({
									...response,
									targetAmount: numberWithSpaces(Number(response.targetAmount) / 100) || 0,
									currentAmount: numberWithSpaces(Number(response.currentAmount) / 100) || 0,
								});
								setOpen(true);
							}
						}}
					>
						{rowData.name}
					</AppLink>
				);
			},
		},
		{
			title: t('repository.goals.goalTitle'),
			field: 'title',
		},
		{},
		{
			title: t('location.headers.connect'),
			headerStyle: { textAlign: 'right' },
			sorting: false,
			render: (rowData) => (
				<IconButton
					edge='start'
					color='primary'
					aria-label='menu'
					onClick={() => connectGoal(rowData.id)}
					style={{ marginLeft: 'auto', display: 'block' }}
				>
					<ConnectIcon />
				</IconButton>
			),
		},
	];

	const EntityActions: IOverrideEntityActions<IGoalBasicDto, IGoalBasicDto> = {
		api: LocationsServiceAPI,
		presetDataCallback: (data: any) => {
			data.currentAmount = Number(parseFloat(data.currentAmount.replace(/ /g, '').replace(',', '.')).toFixed(2)) * 100;
			data.targetAmount = Number(parseFloat(data.targetAmount.replace(/ /g, '').replace(',', '.')).toFixed(2)) * 100 || 0;
			return data;
		},
		getTableData: async function (query: Query<IGoalBasicDto>) {
			const result: QueryResult<IGoalBasicDto> = {
				data: [],
				page: 0,
				totalCount: 0,
			};
			const request: ISearchBaseDto = {
				itemsPerPage: query.pageSize,
				pageNumber: query.page ? query.page + 1 : 1,
				searchPhrase: query.search,
				sortColumn: '',
				sortDirection: '',
			};

			if (query.orderBy) request.sortColumn = query.orderBy.field as string;

			if (query.orderDirection) request.sortDirection = query.orderDirection;

			try {
				const response = await EntityActions.api.GetLocationGoalsToConnect(id, request);

				if (response.success) {
					result.data = response.data.items;
					result.page = response.data.currentPage - 1;
					result.totalCount = response.data.totalItemsCount;

					return result;
				} else {
					let message = t(`errorCodes.${response.errors[0].errorCode}`);
					enqueueSnackbar(`${t('common.errorOccured')}: ${message}`, { variant: 'error' });
				}
			} catch (e) {
				errorDialog(e.message);
			}
			return result;
		},
	};

	const tableProps: any = {
		columns: headers,
		options: { search: false },
	};

	let entityPageProps = {
		tableRef: tableRef,
		EntityActions: EntityActions,
		headers: headers,
		pageTitle: t('location.selectGoal'),
		tableProps: tableProps,
		isDetails: false, //props.isInDetails,
		disableAddNew: true,
		emptyDataSourceMessage: t('location.connectModal.emptyMessage'),
		defaultPerPage: 5,
		//firstColumn: props.isInDetails ? 'name' : undefined,
	};

	function refreshTable() {
		tableRef.current && (tableRef.current! as any).onQueryChange();
	}

	const callbacks: onSubmitProps = {
		presetDataCallback: EntityActions.presetDataCallback,
		setModalOpen: (e) => setOpen(e),
		api: GoalServiceAPI,
		refreshTable: refreshTable,
		setEntity: () => refreshTable(),
	};

	return (
		<>
			<Dialog open={isOpen}>
				<EntityPage {...entityPageProps} />
				<DialogActions>
					<Button onClick={() => onClose()} color='primary' variant='outlined'>
						{t('common.cancel')}
					</Button>
				</DialogActions>
			</Dialog>

			{open && (
				<Modal
					form={<GoalForm />}
					isOpen={open}
					callbacks={callbacks}
					entity={entity}
					modalTitle={t('repository.goals.editGoal')}
					withFileUpload
					withAdditionalFileUpload
					warningMessage={t('repository.goals.popupMessage')}
					fileLabel={t('repository.goals.mainPicture')}
					additionalFileLabel={t('repository.goals.additionalPicture')}
					imageType={DictImageType.Goal}
					imageDimensions={imageDimensions.mainGoalImage}
				/>
			)}
		</>
	);
}
